import UUID from 'util/UUID';
import i18n from 'i18next';
import {
    AssetType, Asset, TextAssetProperties, ImageAssetProperties, AssetProperties, ScaleMode,
    NewsAssetProperties, DateTimeAssetProperties, ClockAssetProperties, ThemeAssetProperties,
    WeatherAssetProperties, StocksAssetProperties, HtmlAssetProperties,
    DirectionAssetProperties, LogoAssetProperties, FloorAssetProperties, SymbolAssetProperties,
    TrafficAssetProperties,
    NewsFormat,
    TextPIAssetProperties,
    TextSource,
    LangCode,
    ImageSource,
} from 'shared/models';

export const getDefaultNewsProviderByCountry = (country?: string) => {
    switch (country) {
        case 'IT': return 'rai';
        case 'CA': return 'global';
        case 'US': return 'cnbc';
        default: return '';
    }
};

export function getDefaultProperties(assetType: AssetType, themeWidth: number, themeHeight: number, width: number, height: number, minTextHeight?: number, country?: string): AssetProperties {
    const aspectRatio = (width || 0.0001) / (height || 0.0001);

    const shortDimension = Math.min(themeWidth, themeHeight);
    const scale = (n: number) => (n * (shortDimension || 480) / 480) / themeHeight;

    const category = getDefaultNewsProviderByCountry(country);

    const shadowProps = {
        shadowColor: 'rgba(0, 0, 0, 0)',
        shadowOffsetX: 0,
        shadowOffsetY: 2,
        shadowBlur: 2,
    };

    switch (assetType) {
        case AssetType.Text:
            return {
                font: 'Open Sans:400',
                size: scale(24),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'left',
                valign: 'top',
                text: '',
                duration: 10,
                textSource: TextSource.Custom,
                ...shadowProps,
            } as TextAssetProperties;

        case AssetType.Image:
            return {
                filename: '',
                halign: 'center',
                valign: 'center',
                mode: ScaleMode.Cover,
                duration: 10,
                imageSource: ImageSource.Custom,
                width: 0,
                height: 0,
            } as ImageAssetProperties;

        case AssetType.Video:
            return {
                filename: '',
                halign: 'center',
                valign: 'center',
                duration: 10,
            } as AssetProperties;

        case AssetType.Rtsp:
            return {
                url: '',
            } as AssetProperties;

        case AssetType.Audio:
            return {
                filename: '',
            } as AssetProperties;

        case AssetType.News:
            return {
                format: NewsFormat.Backdrop,
                category,
                channelId: '',
                headline: {
                    color: 'rgba(255, 255, 255, 1)',
                    size: scale(15),
                    font: 'Open Sans:400',
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                },
                content: {
                    color: 'rgba(0, 0, 0, 1)',
                    size: scale(12),
                    font: 'Open Sans:400',
                    backgroundColor: 'rgba(222, 220, 217, 0.5)',
                },
                visibleCount: 1,
                storyDuration: 10,
                duration: 10,
            } as NewsAssetProperties;

        case AssetType.DateTime:
            return {
                font: 'Open Sans:400',
                size: scale(48),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'center',
                valign: 'center',
                format: 'h:mm AA',
                dateFormat: '(none)',
                useDst: true,
                timezoneOffset: -300, // Default to America/Toronto // TODO: default to timezone that building is in
                duration: 10,
            } as DateTimeAssetProperties;

        case AssetType.Clock:
            return {
                font: 'Open Sans:400',
                size: scale(68),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'center',
                valign: 'center',
                format: 'h:mm AA',
                useDst: true,
                timezoneOffset: -300, // Default to America/Toronto // TODO: default to timezone that building is in
                duration: 10,
                faceColor: "rgb(0,0,0)",
                tickColor: "rgb(255,255,255)",
                hourHandColor: "rgb(255,255,255)",
                minuteHandColor: "rgb(255,255,255)",
                outlineColor: "rgb(0, 0, 0)",
            } as ClockAssetProperties;

        case AssetType.Theme:
            return {
                themeId: null,
                halign: 'center',
                valign: 'center',
                mode: ScaleMode.Fit,
                duration: 10,
                width: 800,
                height: 600,
            } as ThemeAssetProperties;

        case AssetType.Weather:
            // Guess at how many days to display based on the aspect ratio (e.g. how wide or how tall) the asset is
            const days = Math.max(0, Math.min(Math.round(aspectRatio < 1 ? 1 / aspectRatio : aspectRatio) - 1, 5));

            return {
                current: true,
                days,
                location: '', // TODO: default to the city & state (or postal code) the building is in
                style: 'vector',
                format: 'advanced',
                orientation: aspectRatio >= 1 ? 'horizontal' : 'vertical',
                color: '#FFF', // TODO: something that contrasts with the background
                font: 'Open Sans:400',
                halign: 'center',
                valign: 'center',
                duration: 10,
                langCode: LangCode.EN_US,
            } as WeatherAssetProperties;

        case AssetType.Stocks:
            return {
                symbols: 'AAPL,AMZN,GOOG,MSFT,AA,AXP,BA,BAC,CAT,CSCO,CVX,DD,HD,HPQ,IBM,JNJ,JPM,KO,MCD,MMM,MRK,PFE,PG,T,TRV,VZ,WMT,XOM',
                format: 'list',
                color: '#DDD', // TODO: something that contrasts with the background
                gainColor: '#0D0',
                lossColor: '#F44',
                size: scale(18),
                font: 'Open Sans:400',
                halign: 'left',
                valign: 'top',
                duration: 10,
                provider: 'default',
            } as StocksAssetProperties;

        case AssetType.Html:
            return {
                html: '',
                duration: 10,
            } as HtmlAssetProperties;

        case AssetType.MaxPersons:
            return {
                font: 'Open Sans:400',
                size: scale(48),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'right',
                valign: 'top',
                preview: i18n.t('THEME_EDITOR.PI.MAX_PERSONS_PREVIEW_DEFAULT'),
                minTextHeight,
                ...shadowProps,
            } as TextPIAssetProperties;

        case AssetType.MaxWeight:
            return {
                font: 'Open Sans:400',
                size: scale(48),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'left',
                valign: 'top',
                preview: i18n.t('THEME_EDITOR.PI.MAX_WEIGHT_PREVIEW_DEFAULT'),
                minTextHeight,
                ...shadowProps,
            } as TextPIAssetProperties;

        case AssetType.ElevatorId:
            return {
                font: 'Open Sans:400',
                size: scale(48),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'center',
                valign: 'bottom',
                preview: i18n.t('THEME_EDITOR.PI.ELEVATOR_ID_PREVIEW_DEFAULT'),
                minTextHeight,
                ...shadowProps,
            } as TextPIAssetProperties;

        case AssetType.CeNumber:
            return {
                font: 'Open Sans:400',
                size: scale(48),
                color: '#fff',
                halign: 'left',
                valign: 'center',
                preview: '0000',
                minTextHeight,
                ...shadowProps,
            } as TextPIAssetProperties;

        case AssetType.FreeText1:
        case AssetType.FreeText2:
        case AssetType.FreeText3:
        case AssetType.FreeText4:
        case AssetType.Details1:
        case AssetType.Details2:
        case AssetType.Details3:
        case AssetType.Details4:
            return {
                font: 'Open Sans:400',
                size: scale(32),
                color: '#fff',
                halign: 'left',
                valign: 'top',
                preview: '',
                minTextHeight,
                ...shadowProps,
            } as TextPIAssetProperties;
            
        case AssetType.Floor:
            return {
                font: 'Roboto:700',
                size: scale(180),
                color: '#fff', // TODO: something that contrasts with the background
                halign: 'center',
                valign: 'center',
                preview: i18n.t('THEME_EDITOR.PI.FLOOR_PREVIEW_DEFAULT'),
                minTextHeight,
                ...shadowProps,
            } as FloorAssetProperties;

        case AssetType.Direction:
            return {
                style: 'chevron-1',
                color: '#fff', // TODO: something that contrasts with the background
                preview: 'both',
                stretch: false,
                ...shadowProps,
            } as DirectionAssetProperties;
        
        case AssetType.Logo:
            return {
                logo: 'no-smoking',
                halign: 'center',
                valign: 'center',
                color: 'rgba(0,0,0,1)',
                ...shadowProps,
            } as LogoAssetProperties;
        
        case AssetType.Symbol:
            return {
                symbol: 'call',
                halign: 'center',
                valign: 'center',
            } as SymbolAssetProperties;
    
        case AssetType.Traffic:
            return {
                provider: 'here',
                zoom: '12',
            } as TrafficAssetProperties;
            
        default:
            throw new Error('Unhandled assetType: ' + assetType);
            //return {} as AssetProperties;
    }
}

export const makeNewAsset = (assetType: AssetType, themeWidth: number, themeHeight: number, width: number = 0, height: number = 0, minTextHeight?: number, country?: string): Asset => ({
    id: UUID.generate(),
    displayName: null,
    assetType,
    properties: getDefaultProperties(assetType, themeWidth, themeHeight, width, height, minTextHeight, country),
});
